import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useState, useEffect } from "react";
import liff from "@line/liff";
import axios from "axios";
import { ensureLiffInit } from "../../liffClient";
import { colors, fontFamily } from "../../theme/palette";

interface Props {
  refreshKey?: number;
}

export default function WordCard({ refreshKey }: Props) {
  const [userId, setUserId] = useState<string | null>(null);
  const [wordResponse, setWordResponse] = useState<any | null>({
    Item: { words: [] },
  });

  useEffect(() => {
    const fetchWordData = async () => {
      try {
        await ensureLiffInit();
        const profile = await liff.getProfile();
        const userId = profile.userId;
        setUserId(userId);
        const response = await axios.post("/v1/api/word/fetch", {
          userId: profile.userId,
        });

        if (!response.data.Item) {
          return;
        }

        setWordResponse(response.data);
      } catch (error) {
        console.error("Error fetching word data:", error);
      }
    };

    fetchWordData();
  }, [refreshKey]);

  const speakText = (word: string) => {
    const instance = new SpeechSynthesisUtterance(word);
    instance.lang = wordResponse.Item.studying_language;
    window.speechSynthesis.speak(instance);
  };

  const deleteText = (word: string, index: string) => {
    axios
      .post("/v1/api/word/delete", {
        userId: userId,
        word: word,
      })
      .then(() => {
        const block = document.getElementById(word + "_" + index);
        block?.remove();
      })
      .catch((error) => {
        console.error("Error deleting word:", error);
      });
  };

  return (
    <Box>
      {(wordResponse.Item?.words ?? []).map((item: any, i: number) => (
        <Box
          id={item.word + "_" + i}
          key={i}
          sx={{
            backgroundColor: colors.card,
            borderRadius: "18px",
            mb: 2.5,
            boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
            border: `1px solid ${colors.border}`,
            overflow: "hidden",
            transition: "box-shadow 0.2s, transform 0.2s",
            "&:hover": {
              boxShadow: `0 10px 24px -12px rgba(${colors.accentRgb},0.35)`,
              transform: "translateY(-2px)",
            },
            display: "flex",
            alignItems: "stretch",
          }}
        >
          {/* Left accent bar */}
          <Box
            sx={{
              width: 5,
              background: colors.accent,
              flexShrink: 0,
            }}
          />

          {/* Main content */}
          <Box
            sx={{
              flex: 1,
              p: 2.5,
              display: "flex",
              alignItems: "center",
              gap: 2,
              minWidth: 0,
            }}
          >
            {/* Speaker button */}
            <IconButton
              onClick={() => speakText(item.word)}
              sx={{
                width: 44,
                height: 44,
                background: colors.accent,
                color: "#fff",
                flexShrink: 0,
                "&:hover": {
                  background: colors.accentDark,
                  transform: "scale(1.05)",
                },
                transition: "transform 0.15s",
              }}
            >
              <VolumeUpRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {/* Word & Translation */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  color: colors.textPrimary,
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                  fontFamily,
                }}
              >
                {item.word}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mt: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 1.5,
                    backgroundColor: colors.border,
                    borderRadius: 1,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.88rem",
                    color: colors.textSecondary,
                    fontWeight: 500,
                    wordBreak: "break-word",
                    fontFamily,
                  }}
                >
                  {item.translated}
                </Typography>
              </Box>
            </Box>

            {/* Image */}
            {item.image_url && (
              <Box
                component="img"
                src={item.image_url}
                alt="word-image"
                sx={{
                  width: 72,
                  height: 72,
                  objectFit: "cover",
                  borderRadius: "12px",
                  flexShrink: 0,
                  border: `2px solid ${colors.bg}`,
                }}
              />
            )}

            {/* Delete button */}
            <IconButton
              onClick={() => deleteText(item.word, i.toString())}
              sx={{
                width: 36,
                height: 36,
                color: colors.textFaint,
                flexShrink: 0,
                "&:hover": {
                  color: colors.danger,
                  backgroundColor: colors.dangerSoft,
                },
                transition: "color 0.15s, background-color 0.15s",
              }}
            >
              <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
